import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Clock, MapPin } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import Layout from "../Layout";
import SEOHead from "../SEOHead";
import Breadcrumb from "../Breadcrumb";
import FAQSection from "../FAQSection";
import HeroSection from "./HeroSection";  // HeroSection stays in components
import heroImage from "../assets/touch.jpg"; // Adjust path if needed
import { business } from "../../config/business";
import { openWhatsAppWithMessage } from "../../utils/whatsapp";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const responseSteps = [
  { title: "You Reach Out", text: "Call, WhatsApp or submit the form with your project details." },
  { title: "We Review", text: "Our team reviews your requirement within 24 hours." },
  { title: "Consultation", text: "We discuss scope, site details and design considerations." },
  { title: "Estimate", text: "You receive a project-specific estimate and next steps." },
];

const faqs = [
  { q: "How quickly will I get a response?", a: "We aim to respond within 24 hours of receiving your enquiry, whether via form, phone or WhatsApp." },
  { q: "Can I request a site visit?", a: "For projects of meaningful size, a site visit can be arranged — mention this when you reach out." },
  { q: "Do you serve locations outside Hyderabad?", a: "Yes — we deliver, transport and erect structures across Telangana and Andhra Pradesh from our Hyderabad facility." },
];

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // New handler specifically for phone input to accept only numbers (up to 10 digits)
  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData({ ...formData, phone: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Logging the message to Firebase is best-effort — it must never block or
    // skip handing the message off to WhatsApp, which is the real delivery
    // channel here.
    try {
      const response = await fetch('https://vasavi-fbdf5-default-rtdb.firebaseio.com/contactDetails.json');
      const data = await response.json();
      let maxKey = 0;
      if (data) {
        const keys = Object.keys(data).map(k => parseInt(k)).filter(k => !isNaN(k));
        if (keys.length > 0) {
          maxKey = Math.max(...keys);
        }
      }
      const nextKey = String(maxKey + 1).padStart(3, '0');
      await fetch(`https://vasavi-fbdf5-default-rtdb.firebaseio.com/contactDetails/${nextKey}.json`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          timestamp: new Date().toISOString()
        })
      });
    } catch (error) {
      console.error('Error logging contact message to Firebase:', error);
    }

    setSubmitted(true);
    openWhatsAppWithMessage("New Contact Message — TR Structures Website", {
      "Name": formData.name,
      "Phone": formData.phone,
      "Email": formData.email,
      "Subject": formData.subject,
      "Message": formData.message,
    });
  };

  const contactInfo = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      title: "Our Location",
      content: business.address.line,
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      ),
      title: "Phone Number",
      content: business.phone.display,
      link: business.phone.href,
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      title: "Email Address",
      content: business.email.display,
      link: business.email.href,
    },
  ];

  const services = [
    "Industrial Shed Construction",
    "Prefabricated Buildings",
    "Fabrication Services",
    "Industrial Structures",
    "Shed Fabrication",
    "Site Visits",
    "Quotations / Estimates",
    "Technical Support",
  ];

  return (
    <Layout>
      <SEOHead
        title="Contact TR Structures | Industrial Sheds & Fabrication Hyderabad"
        description="Get in touch with TR Structures for industrial sheds, PEB structures, fabrication services and prefabricated buildings in Hyderabad. Call, WhatsApp or send an enquiry."
        keywords="contact TR Structures, industrial shed contractor Hyderabad, PEB enquiry, fabrication services contact"
      />
      <Breadcrumb items={[{ name: "Contact" }]} />

      <main>
        {/* 1. Hero */}
        <HeroSection
          title="Get In Touch"
          subtitle="We're Here to Help"
          description="Whether you're starting a new industrial project or expanding an existing one, TR Structures is ready to assist."
          backgroundImage={heroImage}
          primaryButtonText="Get Quote"
          primaryButtonLink="/enquiry"
        />

        {/* 2. Trust strip */}
        <section className="bg-primary py-6">
          <div className="container-custom">
            <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-primary-foreground text-center">
              <span className="font-montserrat font-semibold flex items-center gap-2"><Clock size={18} aria-hidden="true" /> Response within 24 hours</span>
              <span className="font-montserrat font-semibold flex items-center gap-2"><ShieldCheck size={18} aria-hidden="true" /> Direct engineering consultation</span>
              <span className="font-montserrat font-semibold flex items-center gap-2"><MapPin size={18} aria-hidden="true" /> Bibinagar, Hyderabad</span>
            </div>
          </div>
        </section>

        {/* 3. WhatsApp quick-contact */}
        <section className="py-8 bg-[#25D366]/10">
          <div className="container-custom text-center">
            <p className="font-montserrat font-semibold text-foreground mb-4">Prefer WhatsApp? Message us directly for the fastest response.</p>
            <a
              href={business.whatsapp.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] text-white px-8 py-4 rounded-lg font-montserrat font-semibold hover:bg-[#20bd5a] transition"
            >
              <FaWhatsapp size={22} aria-hidden="true" /> Chat on WhatsApp
            </a>
          </div>
        </section>

        {/* 4. Contact Section */}
        <section className="section-padding">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Info */}
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={fadeUp}>
                <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-foreground mb-6">
                  Our Contact <span className="text-secondary">Details</span>
                </h2>

                <div className="space-y-6 mb-8">
                  {contactInfo.map((info) => (
                    <div key={info.title} className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary flex-shrink-0">
                        {info.icon}
                      </div>
                      <div>
                        <h3 className="font-montserrat font-bold text-foreground text-1xl md:text-base">{info.title}</h3>
                        {info.link ? (
                          <a href={info.link} className="text-muted-foreground hover:text-primary transition-colors text-1xl md:text-sm">
                            {info.content}
                          </a>
                        ) : (
                          <p className="text-muted-foreground text-base md:text-sm">{info.content}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Services List */}
                <div className="bg-muted rounded-xl p-6">
                  <h3 className="font-montserrat font-bold text-foreground mb-4 text-2xl md:text-base">
                    What You Can Contact Us For:
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {services.map((service) => (
                      <div key={service} className="flex items-center gap-2 text-1xl md:text-sm text-muted-foreground">
                        <svg className="w-4 h-4 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {service}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Google Map */}
                <div className="mt-8 rounded-xl overflow-hidden shadow-lg">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3807.111111111111!2d78.583056!3d17.398056!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTfCsDIzJzUzLjAiTiA3OMKwMzUnMDAuMCJF!5e0!3m2!1sen!2sin!4v1234567890"
                    width="100%"
                    height="300"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="TR Structures Location"
                  ></iframe>
                </div>
              </motion.div>

              {/* Contact Form */}
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }} variants={fadeUp}>
                <h2 className="font-montserrat text-2xl md:text-2xl font-bold text-foreground mb-6">
                  Send Us a <span className="text-primary">Message</span>
                </h2>

                {submitted ? (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-6 md:p-10 text-center">
                    <div className="w-16 h-16 md:w-20 md:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 md:w-10 md:h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="font-montserrat text-4xl md:text-2xl font-bold text-green-800 mb-2">
                      Thank You!
                    </h3>
                    <p className="text-green-700 text-lg md:text-base">Your message has been sent successfully. We'll get back to you within 24 hours. We've also opened WhatsApp with your message pre-filled — hit send there for the fastest response.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block font-medium text-foreground mb-2 text-1xl md:text-base">Full Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-1xl md:text-base"
                          placeholder="Your Name"
                        />
                      </div>
                      <div>
                        <label className="block font-medium text-foreground mb-2 text-1xl md:text-base">Phone Number *</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handlePhoneChange}  // Updated to use phone-specific handler
                          required
                          maxLength={10}  // Optional: Enforce max length in input
                          className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-1xl md:text-base"
                          placeholder="XXXXXXXXXX"  // Updated placeholder to reflect 10 digits only
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block font-medium text-foreground mb-2 text-1xl md:text-base">Email Address</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-1xl md:text-base"
                        placeholder="your@email.com"
                      />
                    </div>

                    <div>
                      <label className="block font-medium text-foreground mb-2 text-1xl md:text-base">Subject</label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-1xl md:text-base"
                        placeholder="How can we help you?"
                      />
                    </div>

                    <div>
                      <label className="block font-medium text-foreground mb-2 text-1xl md:text-base">Your Message *</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none text-1xl md:text-base"
                        placeholder="Tell us about your project requirements..."
                      />
                    </div>

                    <button type="submit" className="btn-primary w-full py-4 text-1xl md:text-lg font-medium">
                      Send Message
                    </button>
                  </form>
                )}
              </motion.div>
            </div>
          </div>
        </section>

        {/* 5. Response process */}
        <section className="section-padding bg-muted">
          <div className="container-custom">
            <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-foreground mb-10 text-center">
              What Happens After You Reach Out
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {responseSteps.map((step, i) => (
                <div key={step.title} className="p-6 bg-card rounded-xl shadow-md text-center card-hover">
                  <p className="text-xs font-montserrat font-bold text-secondary mb-2">STEP {i + 1}</p>
                  <h3 className="font-montserrat font-bold text-foreground mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 6. Locations teaser */}
        <section className="section-padding">
          <div className="container-custom text-center">
            <h2 className="font-montserrat text-2xl font-bold text-foreground mb-4">
              Serving Telangana & Andhra Pradesh
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto mb-8">
              We deliver and erect structures across both states from our Hyderabad facility.
            </p>
            <Link to="/locations" className="btn-outline inline-block px-8 py-3">
              View All Locations
            </Link>
          </div>
        </section>

        {/* 7. FAQ */}
        <FAQSection faqs={faqs} />

        {/* 8. Final CTA */}
        <section className="section-padding bg-primary">
          <div className="container-custom text-center">
            <h2 className="font-montserrat text-2xl md:text-2xl font-bold text-primary-foreground mb-4">
              Let's Build Your Structure the Right Way
            </h2>
            <p className="text-primary-foreground/90 text-1xl md:text-lg max-w-2xl mx-auto mb-8">
              Connect with TR Structures today and begin your journey towards strong, durable, and efficient industrial infrastructure.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/enquiry" className="btn-secondary inline-block px-8 py-4">
                Get Project Estimate
              </Link>
              <a href={business.whatsapp.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-8 py-4 rounded-lg font-montserrat font-semibold hover:bg-[#20bd5a] transition">
                <FaWhatsapp size={20} aria-hidden="true" /> WhatsApp Us
              </a>
            </div>
          </div>
        </section>
      </main>
    </Layout>
  );
};

export default Contact;
